import type { PreferencesModel } from '@/shared/generated'

import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { propOr } from 'lodash/fp'

import { usePreferenceMutation } from '../api/mutations'
import { usePreferencesQuery } from '../api/queries'

export function useStoredFiltersKeys(
	preferenceKey: string
): [string[], (data: string[]) => void] {
	const queryClient = useQueryClient()

	/**
	 * Get saved quick filters keys from preferences
	 */
	const preferencesQuery = usePreferencesQuery<string[]>({
		select: propOr([], preferenceKey) as (data?: PreferencesModel) => string[],
	})
	/**
	 * Save quick filters keys to preferences
	 */
	const savePreferencesMutation = usePreferenceMutation(
		{ preferenceType: preferenceKey },
		{
			// Optimistic update
			onMutate: (data) => {
				queryClient.setQueryData(['preferences'], {
					[preferenceKey]: data,
				})
			},
			// Refresh data after mutation
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: ['preferences'],
				})
			},
		}
	)

	return useMemo(
		() => [
			preferencesQuery.data || [],
			(data: string[]) => savePreferencesMutation.mutate(data),
		],
		[preferencesQuery.data, savePreferencesMutation, preferenceKey]
	)
}
